import {
  SUGGESTED_SENDS_PER_HOUR,
  formatE164,
  partnerDisplayName,
  sendWindowWarning,
  smsSegments,
} from "@roof-hawk/core";
import { useEffect, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { navigate, useAsync } from "../../lib/hooks";
import {
  fetchSendQueue,
  markSent,
  unsend,
  updateMessage,
} from "../../lib/partnerQueries";
import { isUninstalledIos } from "../../lib/platform";
import { SEND_CHANNEL, send } from "../../lib/sendChannel";
import { useSession } from "../../session";

/**
 * The rep's screen. One card, one big button, next.
 *
 * Everything about this is built around the loop it lives in: tap Send, iOS
 * swaps to Messages, tap the blue arrow, swipe back to Roof Board. Forty texts
 * is meant to be ten minutes, which only works if the next card is already
 * showing when the rep swipes back — so the card advances on tap rather than on
 * return, and nothing here needs a second decision.
 *
 * What Roof Board cannot know is whether the text actually went. `sent` means
 * Messages opened. Hence the undo, which is on the card for a minute and then
 * is not.
 */

/** How long the "didn't send" way out stays on the card, in seconds. */
const UNDO_SECONDS = 60;

export function SendQueue() {
  const { profileId, displayName } = useSession();
  const queueQuery = useAsync(
    () => (profileId ? fetchSendQueue(profileId) : Promise.resolve([])),
    [profileId],
  );

  const [index, setIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [justSent, setJustSent] = useState<{ id: string; at: number } | null>(
    null,
  );
  const [now, setNow] = useState(() => Date.now());

  // The undo countdown. One timer for the screen, not one per card.
  useEffect(() => {
    if (!justSent) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [justSent]);

  const queue = queueQuery.data ?? [];
  const current = queue[index];
  const warning = sendWindowWarning(new Date());
  const undoSecondsLeft = justSent
    ? Math.max(0, UNDO_SECONDS - Math.floor((now - justSent.at) / 1000))
    : 0;

  if (!profileId) {
    return (
      <div className="list">
        <div className="empty">
          <b>No profile linked to your sign-in</b>
          The send queue is per rep, so there is nothing to show. Ask Jonathon
          to link you.
        </div>
      </div>
    );
  }

  async function sendCurrent(): Promise<void> {
    if (!current) return;
    setError(null);
    try {
      // Marked before Messages opens. Navigating away is the last thing this
      // page does and a write queued behind it does not happen.
      await markSent(current.message.id, profileId);
      setJustSent({ id: current.message.id, at: Date.now() });
      setNow(Date.now());
      setIndex((value) => value + 1);

      const outcome = send(
        SEND_CHANNEL,
        current.partner.phone_mobile,
        current.message.body,
      );
      if (outcome.state === "refused") setError(outcome.reason);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function undoLast(): Promise<void> {
    if (!justSent) return;
    setError(null);
    try {
      await unsend(justSent.id);
      setJustSent(null);
      setIndex((value) => Math.max(0, value - 1));
      await queueQuery.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function skipCurrent(): Promise<void> {
    if (!current) return;
    setError(null);
    try {
      await updateMessage(current.message.id, {
        status: "skipped",
        skip_reason: `Skipped on the send queue by ${displayName}.`,
      });
      setIndex((value) => value + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="send-queue">
      <div className="listhead">
        <div>
          <h2>Send</h2>
          <div className="sub">
            From your phone, from your number. Roof Board never sends these.
          </div>
        </div>
        <span className="count">
          {Math.min(index, queue.length)} of {queue.length}
        </span>
      </div>

      {queue.length > 0 && (
        <div className="send-progress">
          <span
            style={{
              width: `${queue.length ? (Math.min(index, queue.length) / queue.length) * 100 : 0}%`,
            }}
          />
        </div>
      )}

      {warning && <div className="send-warning">{warning}</div>}

      <div className="pacing">
        No more than about {SUGGESTED_SENDS_PER_HOUR} an hour, or the carriers
        start filtering your personal number. A big batch is better spread
        across the day.
      </div>

      <ErrorNote error={error} />

      {queue.length === 0 ? (
        <div className="list">
          <div className="empty">
            <b>Nothing to send</b>
            Approved messages appear here once Jonathon has read the batch.
          </div>
        </div>
      ) : !current ? (
        <div className="list">
          <div className="empty">
            <b>That&apos;s the lot</b>
            {queue.length} {queue.length === 1 ? "message" : "messages"} dealt
            with.
            <div style={{ marginTop: 12 }}>
              <button
                className="btn-ghost"
                onClick={() => {
                  setIndex(0);
                  void queueQuery.reload();
                }}
              >
                Check for more
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="send-card">
          <button
            className="linky"
            onClick={() => navigate(`partner=${current.partner.id}`)}
          >
            {partnerDisplayName(current.partner)}
          </button>
          <div className="sub">{current.partner.company ?? "—"}</div>
          <div className="sub">
            {formatE164(current.partner.phone_mobile) ?? "no mobile"}
          </div>

          <p className="send-body">{current.message.body}</p>
          <div className="sub">
            {current.message.body.trim().length} characters ·{" "}
            {smsSegments(current.message.body)}{" "}
            {smsSegments(current.message.body) === 1 ? "segment" : "segments"}
          </div>

          <button className="btn-send" onClick={() => void sendCurrent()}>
            Send
          </button>
          <button className="btn-small" onClick={() => void skipCurrent()}>
            Skip this one
          </button>
        </div>
      )}

      {justSent && undoSecondsLeft > 0 && (
        <div className="undo-strip">
          <span>Marked sent. Didn&apos;t actually go? {undoSecondsLeft}s</span>
          <button className="btn-small" onClick={() => void undoLast()}>
            Undo — didn&apos;t send
          </button>
        </div>
      )}

      {/* Replies land on the rep's own phone, which Roof Board has no access
          to. The only way an opt-out gets recorded is somebody remembering to
          do it, so the reminder is on the screen where the replies are being
          provoked. */}
      <div className="stop-reminder">
        <b>Anyone reply asking you to stop?</b>
        <p>
          Open them and press <em>They asked me to stop</em> the same day. Roof
          Board cannot see your Messages, so this is the only way it gets
          recorded.
        </p>
      </div>

      {!isUninstalledIos() && queue.length > 0 && <DesktopHandoff />}
    </div>
  );
}

/**
 * Getting the queue onto the phone.
 *
 * There is no desktop sending — an `sms:` link on a Mac opens Messages on the
 * Mac, which sends from iMessage rather than from the rep's phone number, and
 * a partner who has that number saved would see it arrive from an email
 * address. So the desktop's job is to hand the queue over.
 *
 * A QR code without a library: the URL is short and this is a link and a
 * sentence, which does the same job as a scan and cannot go stale.
 */
function DesktopHandoff() {
  const url = `${window.location.origin}/#partners-send`;
  return (
    <div className="handoff">
      <b>Sending happens on your phone</b>
      <p>
        Open Roof Board there and go to Send. From a desktop the text would go
        out as an iMessage from your email address, not from your number.
      </p>
      <code>{url}</code>
    </div>
  );
}
